import React, { FC } from "react";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	Typography,
	styled,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Form, Formik } from "formik";

import NewOrganisationModalValidation from "@/components/Admin/AdminDashboard/components/NewOrganisationModalValidation";
import { Translate } from "@/components/Application/Translate";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { CloseButton } from "@/helpers/components/CloseButton";
import CustomCheckbox from "@/helpers/components/CustomFormFields/CustomCheckbox";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";
import CustomSelect from "@/helpers/components/CustomFormFields/CustomSelect";

import { Timezones } from "@/model/Application/Settings/Timezones";
import { CreateCompanyRequest } from "@/model/Company/CreateCompanyRequest";

import { useMutateCreateOrganization } from "@/service/ApplicationMutations";
import { useFetchCompanies } from "@/service/CompanyQueries";
import { useFetchTimezones } from "@/service/Settings/SettingsQueries";

const StyledGrid = styled(Grid)({
	display: "flex",
	padding: "15px 30px 0 30px",
	border: "1px solid #ccc",
	borderRadius: "4px",
	margin: "10px 0px 10px 0",
	width: "100%",
});

interface NewOrganizationModalProps {
	open: boolean;
	handleClose: () => void;
}

const NewOrganizationModal: FC<NewOrganizationModalProps> = ({
	open,
	handleClose,
}) => {
	const {
		data: timezones,
		isLoading: isLoadingTimezones,
		isError: isTimezonesError,
	} = useFetchTimezones();
	const { data: companies, isLoading: isLoadingCompanies } =
		useFetchCompanies();
	const createOrganization = useMutateCreateOrganization();

	const cleanAndSortTimezones = (timezones: Timezones[]) => {
		// Remove duplicates by using a Map, keyed by Identifier
		const uniqueTimezonesMap = new Map();
		timezones.forEach((timezone) => {
			if (!uniqueTimezonesMap.has(timezone.Identifier)) {
				uniqueTimezonesMap.set(timezone.Identifier, timezone);
			}
		});

		// Convert the map back to an array
		const uniqueTimezones = Array.from(uniqueTimezonesMap.values());

		// Sort by HourDelta first, then by Identifier
		uniqueTimezones.sort((a, b) => {
			const deltaComparison = a.HourDelta.localeCompare(b.HourDelta);
			if (deltaComparison !== 0) {
				return deltaComparison;
			}
			return a.Identifier.localeCompare(b.Identifier);
		});

		return uniqueTimezones;
	};

	const handleSubmit = async (values: CreateCompanyRequest) => {
		try {
			await createOrganization(values);
			handleClose();
			AlertRequest.success();
		} catch (error) {
			console.error(error);
			AlertRequest.failed();
		}
	};

	if (
		isLoadingTimezones ||
		isLoadingCompanies ||
		isTimezonesError ||
		!companies ||
		!timezones
	) {
		return null;
	}

	const sortedTimezones = cleanAndSortTimezones(timezones);

	return (
		<Dialog open={open} onClose={handleClose} fullWidth maxWidth={"lg"}>
			<CloseButton onClose={handleClose} />
			<DialogTitle id="alert-dialog-title">
				<Typography variant="h5">
					<Translate String="Settings.NewOrganization.Title">
						Create New Organisation
					</Translate>
				</Typography>
			</DialogTitle>
			<DialogContent
				dividers
				sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}
			>
				<Formik
					initialValues={{
						Name: "",
						ExternalId: "",
						Language: "en",
						Timezone: "Etc/UTC",
						Modules: {
							IsCompetenceActive: true,
							IsLabActive: true,
							IsMaterialHandlingActive: true,
							IsProceduresActive: true,
							IsShiftActive: true,
							IsTaskActive: true,
						},
						ActiveDirectoryGroups: {
							Guest: "",
							Standard: "",
							Supervisor: "",
							Administrator: "",
						},
					}}
					onSubmit={handleSubmit}
					validationSchema={() => NewOrganisationModalValidation(companies)}
				>
					{({ values }) => (
						<Form>
							<Box display="flex" gap="2em">
								<Box width={"50%"}>
									<CustomInput
										label={"Organisation name"}
										name={"Name"}
										type={"text"}
										id={"organisationName"}
										translationString="Settings.NewOrganization.Name"
									/>
									<CustomInput
										label={"External Id (for API usage)"}
										name={"ExternalId"}
										type={"text"}
										id={"externalId"}
										translationString="Settings.NewOrganization.ExternalId"
									/>
									<CustomSelect
										style={{ maxHeight: "40px" }}
										label="Language"
										name="Language"
										id="language"
										translationString="Settings.NewOrganization.Language"
										defaultValue={values.Language}
										fullWidth
									>
										<option value="en">English</option>
										<option value="no">Norwegian</option>
										<option value="pt">Portuguese</option>
									</CustomSelect>
									<CustomSelect
										style={{ maxHeight: "40px" }}
										label="Timezone"
										name="Timezone"
										id="timezone"
										translationString="Settings.NewOrganization.Timezone"
										defaultValue={values.Timezone}
										fullWidth
									>
										{sortedTimezones.map((x) => (
											<option key={x.Identifier} value={x.Identifier}>
												{x.Identifier} ({x.HourDelta.trim()})
											</option>
										))}
									</CustomSelect>
									<Typography
										style={{
											display: "block",
											color: "rgba(0, 0, 0, 0.6)",
											marginTop: "20px",
										}}
									>
										<Translate String={"Settings.NewOrganization.Modules"}>
											Modules
										</Translate>
									</Typography>
									<StyledGrid className="borderisgrey" container spacing={2}>
										<Grid item xs={12} sm={6}>
											<CustomCheckbox
												label="Procedures"
												name="Modules.IsProceduresActive"
												id="procedures"
												type="checkbox"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<CustomCheckbox
												label="Competence"
												name="Modules.IsCompetenceActive"
												id="competence"
												type="checkbox"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<CustomCheckbox
												label="Shift"
												name="Modules.IsShiftActive"
												id="shift"
												type="checkbox"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<CustomCheckbox
												label="Task"
												name="Modules.IsTaskActive"
												id="task"
												type="checkbox"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<CustomCheckbox
												label="Lab"
												name="Modules.IsLabActive"
												id="lab"
												type="checkbox"
											/>
										</Grid>
										<Grid item xs={12} sm={6}>
											<CustomCheckbox
												label="Material Handling"
												name="Modules.IsMaterialHandlingActive"
												id="materialHandling"
												type="checkbox"
											/>
										</Grid>
									</StyledGrid>
								</Box>
								<Box width={"50%"}>
									<Typography
										style={{
											display: "block",
											color: "rgba(0, 0, 0, 0.6)",
											marginBottom: "6px",
										}}
									>
										<Translate
											String={"Settings.NewOrganization.ActiveDirectoryGroups"}
										>
											Active Directory Groups
										</Translate>
									</Typography>
									<Box
										sx={{
											border: "1px solid #ccc",
											borderRadius: "4px",
											padding: "20px 20px 20px 20px",
										}}
									>
										<CustomInput
											type="text"
											id="guest"
											name="ActiveDirectoryGroups.Guest"
											label="Guest"
											translationString="AD.group.guest"
										/>
										<CustomInput
											type="text"
											id="standard"
											name="ActiveDirectoryGroups.Standard"
											label="Standard"
											translationString="AD.group.standard"
										/>
										<CustomInput
											type="text"
											id="supervisor"
											name="ActiveDirectoryGroups.Supervisor"
											label="Supervisor"
											translationString="AD.group.supervisor"
										/>
										<CustomInput
											type="text"
											id="administrator"
											name="ActiveDirectoryGroups.Administrator"
											label="Administrator"
											translationString="AD.group.administrator"
										/>
									</Box>
								</Box>
							</Box>
							<DialogActions>
								<Button type="submit" variant="contained" color="info">
									Create
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>
			<Divider />
		</Dialog>
	);
};

export default NewOrganizationModal;
